/**
 * Section classes du profil avec enseignements
 */
import { useState } from 'react'
import { Accordion, AccordionItem, Tag } from '@carbon/react'
import { Education, Book, User, Trophy } from '@carbon/icons-react'
import type { ClassMembershipDetail } from '@sphere/user/domain'
import { getSubjectCodeAccentHex } from '@sphere/shared/util'
import styles from './profile-classes.module.scss'

interface ProfileClassesProps {
  classes: ClassMembershipDetail[]
}

export function ProfileClasses({ classes }: ProfileClassesProps) {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set())

  const toggleItem = (id: string) => {
    setOpenItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  if (classes.length === 0) {
    return (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>
            <Education size={20} />
            Classes
          </h3>
        </div>
        <p className={styles.empty}>Aucune classe</p>
      </div>
    )
  }

  const classesByEstablishment = classes.reduce(
    (acc, cls) => {
      const key = cls.establishmentId
      if (!acc[key]) {
        acc[key] = {
          establishmentName: cls.establishmentName,
          classes: [],
        }
      }
      acc[key].classes.push(cls)
      return acc
    },
    {} as Record<
      string,
      { establishmentName: string; classes: ClassMembershipDetail[] }
    >
  )

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>
          <Education size={20} />
          Classes & Enseignements
          <Tag type="gray" className={styles.badge}>
            {classes.length}
          </Tag>
        </h3>
      </div>
      <div className={styles.content}>
        {Object.entries(classesByEstablishment).map(
          ([establishmentId, { establishmentName, classes: estClasses }]) => (
            <div key={establishmentId} className={styles.establishmentBlock}>
              <h4 className={styles.establishmentName}>{establishmentName}</h4>
              <Accordion>
                {estClasses.map(cls => (
                  <AccordionItem
                    key={cls.membershipId}
                    title={
                      <div className={styles.accordionTitle}>
                        <div className={styles.accordionTitleContent}>
                          <div className={styles.classNameRow}>
                            <span className={styles.className}>
                              {cls.className}
                            </span>
                            {cls.level && (
                              <Tag type="outline" className="cds--tag--sm">
                                {cls.level.label}
                              </Tag>
                            )}
                            {cls.isHead && (
                              <Tag type="blue" className={styles.delegueTag}>
                                <Trophy size={12} />
                                Délégué
                              </Tag>
                            )}
                          </div>
                          <div className={styles.metaRow}>
                            <Tag
                              type="gray"
                              className={`cds--tag--sm ${
                                cls.role.code === 'STUDENT'
                                  ? styles.roleStudent
                                  : cls.role.code === 'MAIN_TEACHER'
                                    ? styles.roleMainTeacher
                                    : ''
                              }`}
                            >
                              {cls.role.name}
                            </Tag>
                            {cls.teachings.length > 0 && (
                              <span className={styles.metaItem}>
                                <Book size={12} />
                                {cls.teachings.length} matière
                                {cls.teachings.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    }
                    open={openItems.has(cls.membershipId)}
                    onHeadingClick={() => toggleItem(cls.membershipId)}
                  >
                    <div className={styles.accordionContent}>
                      {cls.teachings.length > 0 ? (
                        <div className={styles.teachingsList}>
                          {cls.teachings.map(teaching => (
                            <div
                              key={teaching.id}
                              className={styles.teachingItem}
                            >
                              <div className={styles.teachingLeft}>
                                <div
                                  className={styles.subjectBar}
                                  style={{
                                    backgroundColor: getSubjectCodeAccentHex(
                                      teaching.subjectCode
                                    ),
                                  }}
                                />
                                <div className={styles.teachingInfo}>
                                  <p>{teaching.subjectName}</p>
                                  <p>{teaching.subjectCode}</p>
                                </div>
                              </div>
                              {teaching.teacherName && (
                                <div className={styles.teachingMeta}>
                                  <User size={14} />
                                  <span>{teaching.teacherName}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className={styles.noTeachings}>
                          Aucun enseignement pour cette classe
                        </p>
                      )}
                    </div>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )
        )}
      </div>
    </div>
  )
}
